import { useCallback, useEffect, useMemo, useRef, useState, } from "react";
import { useDispatch, useSelector } from "react-redux";
import LoadingBox from "../../../components/LoadingBox";
import { OpService } from "../../../util/services/operation";
import { selectOpDetail } from "../../../util/store/selectors";
import { showErrorResponse } from "../../../util/varibles/global";
import { IOperation2, ITankProcessData, TOperation2 } from "../../../util/varibles/interface";
import { opDetailActions } from "../reducer";
import ActualRoute from "./ActualRoute";
import { getTimelineFromActivityLog, mergeSensorData } from "./contants";
import OperationSummary from "./OperationSummary";
import TankChart from "./TankChart";
import TimelineRange from "./TimelineRange";

const SENSOR_INTERVAL_TIME = 5 * 60 * 1000;
const SENSOR_CHUNK_TIME = 2 * 24 * 60 * 60 * 1000;

const convertSensorName = (name: string) => name
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const convertSensorDataNames = (
    sensorData: Record<string, ITankProcessData>
): Record<string, ITankProcessData> => Object.entries(sensorData).reduce(
    (convertedData, [tankName, tankData]) => {
        convertedData[tankName] = Object.entries(tankData).reduce(
            (convertedTankData, [sensorType, sensors]) => {
                convertedTankData[sensorType] = Array.isArray(sensors)
                    ? sensors.map(sensor => ({
                        ...sensor,
                        name: convertSensorName(sensor.name),
                    }))
                    : sensors;

                return convertedTankData;
            }, {} as ITankProcessData);

        return convertedData;
    }, {} as Record<string, ITankProcessData>
);

interface ISensorTimeRange {
    start_time: number;
    finish_time: number;
}

interface ISensorState {
    data: Record<string, ITankProcessData>;
    loading: boolean;
}

interface ISensorMetric {
    total_diesel: number;
    total_fuel: number;
}

export interface IAxisRange {
    min: number;
    max: number;
}

const getSensorTimeRanges = (start: number, finish: number): ISensorTimeRange[] => {
    const ranges: ISensorTimeRange[] = [];

    let currentStart = start;

    while (currentStart < finish) {
        const currentFinish = Math.min(currentStart + SENSOR_CHUNK_TIME, finish);

        ranges.push({
            start_time: currentStart,
            finish_time: currentFinish,
        });

        currentStart = currentFinish;
    }

    return ranges;
};

export default function ProcessData() {
    const dispatch = useDispatch();
    const data = useSelector(selectOpDetail.data);

    const { id, tenant_id, } = data?.operation || {};

    const { activity_log = [], } = data || {};

    const { start, finish, } = getTimelineFromActivityLog(data as IOperation2);
    const shouldRunInterval = !!start && !activity_log[activity_log.length - 1]?.real_start_time;
    const [sensorState, setSensorState] = useState<ISensorState>({ data: {}, loading: false, });

    const metricRef = useRef<ISensorMetric>({ total_diesel: 0, total_fuel: 0, });
    const requestVersionRef = useRef(0);
    const fetchingRef = useRef(false);

    const showDateOnXAxis = useMemo(() => {
        const isSameDay = (startTime?: number, finishTime?: number) => {
            if (!startTime || !finishTime) {
                return true;
            }

            const startDate = new Date(startTime);
            const finishDate = new Date(finishTime);

            return (
                startDate.getFullYear() === finishDate.getFullYear() &&
                startDate.getMonth() === finishDate.getMonth() &&
                startDate.getDate() === finishDate.getDate()
            );
        };

        return !isSameDay(start, finish);
    }, [start, finish]);

    const fetchSensorData = useCallback(
        async (finishTime?: number) => {
            if (!start || !id || !tenant_id || fetchingRef.current) {
                return;
            }

            const requestStartTime = start;
            const requestFinishTime = finishTime || finish;

            if (!requestFinishTime || requestStartTime >= requestFinishTime) {
                return;
            }

            const ranges = getSensorTimeRanges(requestStartTime, requestFinishTime);

            if (ranges.length === 0) return;

            const currentRequestVersion = requestVersionRef.current;

            fetchingRef.current = true;

            setSensorState(previousState => ({ ...previousState, loading: true, }));

            try {
                const results = await Promise.all(
                    ranges.map(range =>
                        OpService.getSensorData({
                            id,
                            tenant_id,
                            start_time: range.start_time,
                            finish_time: range.finish_time,
                        })
                    )
                );

                if (currentRequestVersion !== requestVersionRef.current) {
                    return;
                }

                let mergedData: Record<string, ITankProcessData> = {};

                let totalDiesel = 0;
                let totalFuel = 0;

                results.forEach(result => {
                    const { total_diesel = 0, total_fuel = 0, ...tankData } = result;

                    totalDiesel += total_diesel;
                    totalFuel += total_fuel;

                    mergedData = mergeSensorData(
                        mergedData,
                        convertSensorDataNames(tankData as Record<string, ITankProcessData>),
                    );
                });

                metricRef.current = {
                    total_diesel: totalDiesel,
                    total_fuel: totalFuel,
                };

                setSensorState({ data: mergedData, loading: false, });

            } catch (error) {
                if (currentRequestVersion !== requestVersionRef.current) {
                    return;
                }

                setSensorState(previousState => ({ ...previousState, loading: false, }));

                showErrorResponse("Failed to fetch sensor data", error);
            } finally {
                if (currentRequestVersion === requestVersionRef.current) {
                    fetchingRef.current = false;
                }
            }
        }, [start, finish, id, tenant_id,]
    );

    useEffect(() => {
        requestVersionRef.current += 1;
        fetchingRef.current = false;

        metricRef.current = { total_diesel: 0, total_fuel: 0 };

        setSensorState({ data: {}, loading: false, });

        if (!start || !id || !tenant_id) {
            return;
        }

        fetchSensorData();

        if (!shouldRunInterval) {
            return;
        }

        const interval = window.setInterval(() => {
            fetchSensorData(Date.now());

            dispatch(opDetailActions.getActualRoute({ tenant_id, id, }));
        }, SENSOR_INTERVAL_TIME);

        return () => {
            window.clearInterval(interval);

            requestVersionRef.current += 1;
            fetchingRef.current = false;
        };
    }, [id, tenant_id, start, shouldRunInterval, dispatch, fetchSensorData]);

    if (!start) return <LoadingBox className="mt-[50px]" />;

    const { data: sensorData, loading, } = sensorState;

    const sensorKeys = Object.keys(sensorData);

    return (
        <div className="h-[calc(100vh-122px)] overflow-x-hidden overflow-y-auto pt-3">
            <div className="flex flex-col mx-auto gap-4 w-[90%]">
                <TimelineRange
                    operation={data?.operation as TOperation2}
                    activity_log={data?.activity_log || []}
                />

                <OperationSummary {...metricRef.current} />

                <div className="flex flex-col gap-3 min-h-[300px] relative">
                    {(loading ||
                        sensorKeys.length === 0) && (
                            <LoadingBox
                                loading={loading}
                                level="bg"
                                className="w-[calc(100vw-15px)] !left-1/2 -translate-x-1/2"
                                message="Sensor data is empty"
                            />
                        )}

                    {sensorKeys.map(tankName => (
                        <TankChart
                            key={tankName}
                            name={tankName}
                            data={sensorData[tankName]}
                            showDateOnXAxis={showDateOnXAxis}
                        />
                    ))}
                </div>

                <ActualRoute />
            </div>
        </div>
    );
}
